import { DropdownButtonType, List, ListItem, SingleSelectDropdown, Tooltip, TooltipPosition, TooltipProps } from '@tableau/widgets';
import React from 'react';
import { connect, Dispatch } from 'react-redux';

import { Messages } from '../../../codegen/Localize';
import { ExtractEncryptionState } from '../../../ts/models/IDataSourceInfo';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { encryptContent, EncryptionActionType } from '../../actions/spaces/EncryptContentAction';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { ExtractEncryptionMode } from '../../../sites/SitePropertyTypes';

export enum ListItemValue {
  unencrypted = 'unencrypted',
  encrypted = 'encrypted'
}

export class EncryptionStatusDropdown extends React.PureComponent<IEncryptionStatusDropdownProps, IEncryptionStatusDropdownState> {
  private selectedOption: ListItemValue;

  constructor(props: IEncryptionStatusDropdownProps) {
    super(props);
    this.state = {
      inProgress: false
    };
    this.onChange = this.onChange.bind(this);
  }

  onChange(value: ListItemValue): void {
    if (this.selectedOption !== value) {
      this.setState({
        inProgress: true
      });
      this.props.dispatchEncryptionAction(
        this.props.itemInfo,
        value === ListItemValue.encrypted ? EncryptionActionType.encryptExtracts : EncryptionActionType.decryptExtracts
      );
    }
  }

  public render(): React.ReactNode {
    const encryptionDropdownOptions: ListItem[] = [
      { value: ListItemValue.unencrypted, content: Messages.encryptionDropdownOptionUnencrypted() },
      { value: ListItemValue.encrypted, content: Messages.encryptionDropdownOptionEncrypted() },
    ];
    const isExtractEncryptionModeDisabled: boolean = this.props.siteEARMode === ExtractEncryptionMode.DISABLED;
    const stateToStatusCopyMap: {
      [key: string]: string;
    } = {};
    stateToStatusCopyMap[ExtractEncryptionState.encrypted] = Messages.encryptionDropdownStatusEncrypted();
    stateToStatusCopyMap[ExtractEncryptionState.unencrypted] = isExtractEncryptionModeDisabled
      ? Messages.encryptionDropdownStatusUnencryptedWhenModeIsDisabled()
      : Messages.encryptionDropdownStatusUnencrypted();
    stateToStatusCopyMap[ExtractEncryptionState.pendingEncryption] = Messages.encryptionDropdownStatusEncrypting();
    stateToStatusCopyMap[ExtractEncryptionState.pendingDecryption] = Messages.encryptionDropdownStatusDecrypting();
    this.selectedOption = determineCurrentSelection(this.props.itemInfo.extractEncryptionState);

    const tooltipProps: TooltipProps = {
      getContent: () => <span>{Messages.encryptionTooltip()}</span>,
      position: TooltipPosition.Right
    };

    const encryptionStatusSpan: React.ReactElement = <span className={`disabled`}>{stateToStatusCopyMap[this.props.itemInfo.extractEncryptionState]}</span>;
    /* eslint-disable jsx-a11y/anchor-is-valid */
    const encryptionStatusAnchor: React.ReactElement = <a className={SpaceHeaderStyles.headerLink}>{stateToStatusCopyMap[this.props.itemInfo.extractEncryptionState]}</a>;

    return (
      <div className={SpaceHeaderStyles.encryptionStatus}>
        {this.props.disabled ? (
          isExtractEncryptionModeDisabled
            ? encryptionStatusSpan
            : <Tooltip {...tooltipProps}>{encryptionStatusSpan}</Tooltip>
        ) : (
          <SingleSelectDropdown
            type={DropdownButtonType.Text}
            list={<List items={encryptionDropdownOptions} selectedValue={this.selectedOption} />}
            value={this.selectedOption}
            onChange={this.onChange}
            disabled={this.state.inProgress}
            label={''}
          >
            {isExtractEncryptionModeDisabled
              ? encryptionStatusAnchor
              : <Tooltip {...tooltipProps}>{encryptionStatusAnchor}</Tooltip>
            }
          </SingleSelectDropdown>
          )}
      </div>
    );
  }
}

function determineCurrentSelection(state: ExtractEncryptionState): ListItemValue {
  switch (state) {
    case ExtractEncryptionState.encrypted:
    case ExtractEncryptionState.pendingEncryption:
      return ListItemValue.encrypted;
    case ExtractEncryptionState.unencrypted:
    case ExtractEncryptionState.pendingDecryption:
      return ListItemValue.unencrypted;
  }
}

export interface IEncryptionStatusDropdownState {
  inProgress: boolean;
}

export interface IEncryptionStatusDropdownOwnProps {
  itemInfo: ISpaceItemDetailInfo<'workbook' | 'datasource' | 'flow'>;
  disabled: boolean;
}

export interface IEncryptionStatusDropdownStateProps {
  siteEARMode: ExtractEncryptionMode;
}

export interface IEncryptionStatusDropdownDispatchProps {
  dispatchEncryptionAction?: (itemInfo: ISpaceItemDetailInfo<'workbook' | 'datasource' | 'flow'>, encryptionType: EncryptionActionType) => void;
}

export type IEncryptionStatusDropdownProps = IEncryptionStatusDropdownOwnProps & IEncryptionStatusDropdownStateProps & IEncryptionStatusDropdownDispatchProps;

function mapDispatchToProps(dispatch: Dispatch<AppState>): IEncryptionStatusDropdownDispatchProps {
  return {
    dispatchEncryptionAction: (itemInfo: ISpaceItemDetailInfo<'workbook' | 'datasource' | 'flow'>, encryptionType: EncryptionActionType) => dispatch(encryptContent([itemInfo], encryptionType))
  };
}

const mapStateToProps: MapStateToProps<IEncryptionStatusDropdownStateProps> = (state: AppState) => {
  return {
    siteEARMode: SessionSiteSelectors.extractEncryptionMode(state)
  };
};

export const ConnectedEncryptionStatusDropdown = connect(mapStateToProps, mapDispatchToProps)(EncryptionStatusDropdown);
